/**
 * Persists MeasurementWizard UI state in IndexedDB so it survives page reload / tab close.
 * Separate from the CRM sync outbox (which stores pending server mutations).
 */
import type { GateMeasurement, DealProductItem } from "@shared/api";

const DB_NAME = "tpt-crm-measurement-draft";
const DB_VERSION = 1;
const STORE = "drafts";

export type MeasurementWizardDraft = {
  dealId: string;
  updatedAt: number;
  currentStep: number;
  useProductFlow: boolean;
  selectedProductTypes: string[];
  countByType: Record<string, number>;
  products: DealProductItem[];
  gates: GateMeasurement[];
  gateCount: number;
  currentGateIndex: number;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "dealId" });
      }
    };
  });
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
  });
}

export async function saveMeasurementWizardDraft(draft: MeasurementWizardDraft): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    await idbRequest(tx.objectStore(STORE).put(draft));
    db.close();
  } catch {
    /* non-critical */
  }
}

export async function loadMeasurementWizardDraft(dealId: string): Promise<MeasurementWizardDraft | null> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const result = (await idbRequest(tx.objectStore(STORE).get(dealId))) as MeasurementWizardDraft | undefined;
    db.close();
    return result ?? null;
  } catch {
    return null;
  }
}

export async function deleteMeasurementWizardDraft(dealId: string): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    await idbRequest(tx.objectStore(STORE).delete(dealId));
    db.close();
  } catch {
    /* non-critical */
  }
}
